// Central client-side store for the Degree master list.
// - Fetches once from `/api/public/degrees` and caches in module memory.
// - Any component that mutates degrees (Admin Manage Degrees) calls
//   `refreshDegrees()` after a successful write so every dropdown across
//   the app reflects the change without a page reload.
// - Subscribers get a `{ degrees, loading }` snapshot on every change.

import { useEffect, useSyncExternalStore } from "react";
import { api } from "./api";

const state = {
    degrees: [],
    loading: false,
    loaded: false,
    inflight: null,
};
const listeners = new Set();

const emit = () => listeners.forEach((l) => l());

const subscribe = (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
};

const getSnapshot = () => state;

// One shared fetch — repeated callers piggyback on the in-flight promise.
async function fetchOnce() {
    if (state.inflight) return state.inflight;
    state.loading = true;
    emit();
    state.inflight = (async () => {
        try {
            const { data } = await api.get("/public/degrees");
            state.degrees = Array.isArray(data) ? data : [];
            state.loaded = true;
        } catch (_) {
            state.degrees = [];
        } finally {
            state.loading = false;
            state.inflight = null;
            emit();
        }
    })();
    return state.inflight;
}

export function ensureDegreesLoaded() {
    if (!state.loaded && !state.inflight) fetchOnce();
}

export async function refreshDegrees() {
    state.inflight = null;
    state.loaded = false;
    return fetchOnce();
}

/**
 * React hook that returns the current degree list.
 * @returns {{ degrees: Array, loading: boolean, loaded: boolean }}
 */
export function useDegrees() {
    const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    useEffect(() => {
        ensureDegreesLoaded();
    }, []);
    return snap;
}

// Grouped view: [{ level, items }] preserving admin-defined order.
export function groupByLevel(degrees) {
    const groups = { "Bachelor's": [], "Master's": [] };
    degrees.forEach((d) => {
        if (groups[d.level]) groups[d.level].push(d);
    });
    return [
        { level: "Bachelor's Degrees", items: groups["Bachelor's"] },
        { level: "Master's Degrees", items: groups["Master's"] },
    ];
}

// The visible label used everywhere: "Bachelor of Engineering (BE)".
export function degreeLabel(d) {
    if (!d) return "";
    return d.abbreviation ? `${d.name} (${d.abbreviation})` : d.name;
}

// Accent-strip + lower for search normalisation.
const norm = (s) =>
    (s || "")
        .toString()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

/**
 * Rank matches for a search query. Returns filtered+sorted list; empty
 * query returns the full list untouched (grouping caller handles order).
 *
 * Ranking (lower score = better):
 *   0 exact abbreviation match       ("mba"    → MBA)
 *   1 abbreviation prefix            ("mb"     → MBA, MBBS)
 *   2 name-word prefix               ("comp"   → Computer Science)
 *   3 abbreviation substring         ("s"      → BSc, MSc, MSE…)
 *   4 name substring                 ("engineer"→ all engineering)
 */
export function searchDegrees(degrees, query) {
    const q = norm(query);
    if (!q) return degrees.slice();
    const scored = [];
    for (const d of degrees) {
        const name = norm(d.name);
        const abbr = norm(d.abbreviation);
        const words = name.split(/\s+/);
        let score = -1;
        if (abbr === q) score = 0;
        else if (abbr.startsWith(q)) score = 1;
        else if (words.some((w) => w.startsWith(q))) score = 2;
        else if (abbr.includes(q)) score = 3;
        else if (name.includes(q)) score = 4;
        if (score >= 0) scored.push({ d, score });
    }
    scored.sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return a.d.name.localeCompare(b.d.name);
    });
    return scored.map((s) => s.d);
}

// Flat list of every canonical "Name (Abbr)" label used for detecting
// "Others" free-text entries when displaying stored applicant values.
export function allDegreeLabels(degrees) {
    return degrees.map(degreeLabel);
}
